import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useViewModeStore } from '../../stores/viewModeStore';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';

interface ManagerViewToggleProps {
    open?: boolean;
}

const ManagerIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className={className} fill="currentColor">
    <path d="M320 312C253.7 312 200 258.3 200 192C200 125.7 253.7 72 320 72C386.3 72 440 125.7 440 192C440 258.3 386.3 312 320 312zM289.5 368L350.5 368C360.2 368 368 375.8 368 385.5C368 389.7 366.5 393.7 363.8 396.9L336.4 428.9L367.4 544L368 544L402.6 405.5C404.8 396.8 413.7 391.5 422.1 394.7C484 418.3 528 478.3 528 548.5C528 563.6 515.7 575.9 500.6 575.9L139.4 576C124.3 576 112 563.7 112 548.6C112 478.4 156 418.4 217.9 394.8C226.3 391.6 235.2 396.9 237.4 405.6L272 544.1L272.6 544.1L303.6 429L276.2 397C273.5 393.8 272 389.8 272 385.6C272 375.9 279.8 368.1 289.5 368.1z"/>
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className={className} fill="currentColor">
    <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z"/>
  </svg>
);

const ManagerViewToggle: React.FC<ManagerViewToggleProps> = ({ open = true }) => {
    const { viewMode, toggleViewMode } = useViewModeStore();
    const { managerViewEnabled } = useManagerViewPermission();

    const rbacRole = (
      localStorage.getItem('rbac_role') ||
      (() => {
        try {
          const perms = JSON.parse(localStorage.getItem('modular_permissions') || '{}');
          return perms?._roleName || '';
        } catch { return ''; }
      })()
    ).toLowerCase();
    const isManager = rbacRole === 'manager' || rbacRole === 'admin' || rbacRole === 'super_admin';

    if (!isManager) return null;
    if (!managerViewEnabled) return null;


    const isManagerView = viewMode === 'manager';

    return (
        <div
            className={`px-3 py-2 border-b border-white/10 shrink-0 overflow-hidden select-none transition-colors duration-300 ${open ? '' : 'flex justify-center'}`}
            title={isManagerView ? 'Switch to User View (see only your own data)' : 'Switch to Manager View (see all team data)'}
        >
            {open ? (
                /* Expanded: label + pill toggle */
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center justify-between gap-2"
                >
                    <div className="flex flex-col min-w-0">
                        <span className="text-[10px] uppercase tracking-widest text-white/40 font-semibold leading-none mb-1">
                            View Mode
                        </span>
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={viewMode}
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: 0.15 }}
                                className={`text-xs font-semibold whitespace-nowrap ${
                                    isManagerView ? 'text-indigo-300' : 'text-white/60'
                                }`}
                            >
                                {isManagerView ? 'Manager View' : 'User View'}
                            </motion.span>
                        </AnimatePresence>
                    </div>

                    {/* Toggle pill */}
                    <button
                        onClick={toggleViewMode}
                        role="switch"
                        aria-checked={isManagerView}
                        aria-label="Toggle between Manager View and User View"
                        className={`
                            relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full
                            transition-colors duration-300 focus:outline-none
                            focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
                            focus-visible:ring-offset-[#1e4a7f]
                            ${isManagerView ? 'bg-indigo-500' : 'bg-white/20'}
                        `}
                    >
                        <motion.span
                            layout
                            animate={{ x: isManagerView ? 22 : 2 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm"
                        >
                            <AnimatePresence mode="wait">
                                {isManagerView ? (
                                    <motion.span
                                        key="manager"
                                        initial={{ opacity: 0, scale: 0.6 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.6 }}
                                        transition={{ duration: 0.12 }}
                                    >
                                        <ManagerIcon className="h-3.5 w-3.5 text-indigo-600" />
                                    </motion.span>
                                ) : (
                                    <motion.span
                                        key="user"
                                        initial={{ opacity: 0, scale: 0.6 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.6 }}
                                        transition={{ duration: 0.12 }}
                                    >
                                        <UserIcon className="h-3.5 w-3.5 text-slate-400" />
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </motion.span>
                    </button>
                </motion.div>
            ) : (
                /* Collapsed: icon-only button centered */
                <button
                    onClick={toggleViewMode}
                    aria-label="Toggle View Mode"
                    title={isManagerView ? 'Manager View — click to switch' : 'User View — click to switch'}
                    className={`
                        p-2 rounded-lg transition-all duration-200
                        ${isManagerView
                            ? 'bg-indigo-500/30 text-indigo-300 hover:bg-indigo-500/50'
                            : 'bg-white/10 text-white/50 hover:bg-white/20'
                        }
                    `}
                >
                    {isManagerView
                        ? <ManagerIcon className="h-5 w-5" />
                        : <UserIcon className="h-5 w-5" />
                    }
                </button>
            )}
        </div>
    );
};

export default ManagerViewToggle;
